import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import Swal from "sweetalert2";

import {
    FaBookOpen,
    FaCalendarAlt,
    FaClock,
    FaDownload,
    FaFilePdf,
    FaGraduationCap,
    FaLayerGroup,
    FaTrash,
    FaUserTie,
} from "react-icons/fa";

import { pdf } from "@react-pdf/renderer";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import MyPlanCoursesBillSheetPDF from "./plannedCoursesBillPdf";

const cayepSwal = Swal.mixin({
    confirmButtonColor: "#062A4B",
    cancelButtonColor: "#6B7280",
    background: "#FFFFFF",
    color: "#142536",
});

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    const data = error?.response?.data;

    if (!data) {
        return error?.message || fallback;
    }

    if (typeof data === "string") {
        return data;
    }

    if (data.detail) {
        return data.detail;
    }

    if (data.message) {
        return data.message;
    }

    if (data.error) {
        return data.error;
    }

    if (data.non_field_errors?.length) {
        return data.non_field_errors[0];
    }

    const firstKey = Object.keys(data)[0];

    if (firstKey) {
        const value = data[firstKey];

        if (Array.isArray(value)) {
            return value[0];
        }

        if (typeof value === "string") {
            return value;
        }

        if (
            value &&
            typeof value === "object"
        ) {
            if (value.detail) {
                return value.detail;
            }

            if (value.message) {
                return value.message;
            }
        }
    }

    return fallback;
};

const formatMoney = (value) => {
    const amount = Number(value || 0);

    if (!Number.isFinite(amount)) {
        return "0.00";
    }

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

const getCourseFromPlan = (item) => {
    if (!item) {
        return null;
    }

    return (
        item.course ||
        item.subject ||
        null
    );
};

const getPlanId = (item) => {
    if (!item) {
        return null;
    }

    const value =
        item.id ??
        item.plan_id ??
        item.course_plan_id ??
        null;

    if (value == null) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : null;
};

const getCourseId = (item) => {
    const course =
        getCourseFromPlan(item);

    if (course?.id != null) {
        return Number(course.id);
    }

    if (item?.course_id != null) {
        return Number(item.course_id);
    }

    if (item?.subject_id != null) {
        return Number(item.subject_id);
    }

    return null;
};

const normalizePlans = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    const seen = new Set();

    return items.filter((item) => {
        const planId =
            getPlanId(item);

        const courseId =
            getCourseId(item);

        const identifier =
            planId != null
                ? `plan-${planId}`
                : courseId != null
                ? `course-${courseId}`
                : null;

        if (!identifier) {
            return true;
        }

        if (seen.has(identifier)) {
            return false;
        }

        seen.add(identifier);

        return true;
    });
};

const getCourseTitle = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.course_title ||
        course?.name ||
        item?.course_name ||
        "Untitled Course"
    );
};

const getCourseImage = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.thumbnail ||
        course?.image ||
        null
    );
};

const getCoursePrice = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.current_price ??
        course?.price ??
        item?.current_price ??
        item?.price ??
        item?.amount ??
        0
    );
};

const getCourseFacilitator = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.facilitator ||
        course?.facilitator_name ||
        item?.facilitator_name ||
        item?.teacher_name ||
        "Not assigned"
    );
};

const ViewPlanCoursesList = () => {
    const { selectedYear } =
        useAcademicYear();

    const [plans, setPlans] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [removingId, setRemovingId] =
        useState(null);

    const [search, setSearch] =
        useState("");

    const [refreshing, setRefreshing] =
        useState(false);

    const [pdfLoading, setPdfLoading] =
        useState(false);

    const fetchPlans =
        useCallback(async () => {
            setLoading(true);

            try {
                const response =
                    await api.get(
                        "/api/course-learning/plans/"
                    );

                const data =
                    response.data;

                const items =
                    Array.isArray(data)
                        ? data
                        : data?.items ||
                          data?.results ||
                          data?.plans ||
                          [];

                setPlans(
                    normalizePlans(items)
                );
            } catch (error) {
                console.error(
                    "Planned courses error:",
                    error
                );

                setPlans([]);

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Unable to Load Courses",
                    text: getErrorMessage(
                        error,
                        "Your planned courses could not be loaded."
                    ),
                });
            } finally {
                setLoading(false);
            }
        }, []);

    useEffect(() => {
        fetchPlans();
    }, [
        fetchPlans,
        selectedYear,
    ]);

    const filteredPlans =
        useMemo(() => {
            const value =
                search
                    .trim()
                    .toLowerCase();

            if (!value) {
                return plans;
            }

            return plans.filter(
                (item) => {
                    const course =
                        getCourseFromPlan(
                            item
                        );

                    const searchable = [
                        course?.course_title,
                        course?.name,
                        course?.description,
                        course?.category,
                        course?.level,
                        course?.facilitator,
                        course?.facilitator_name,
                        item?.course_name,
                        item?.facilitator_name,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();

                    return searchable.includes(
                        value
                    );
                }
            );
        }, [
            plans,
            search,
        ]);

    const removeCourse =
        async (item) => {
            const planId =
                getPlanId(item);

            const courseTitle =
                getCourseTitle(item);

            if (!planId) {
                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Unable to Identify Course",
                    text:
                        "The selected course plan could not be identified.",
                });

                return;
            }

            const result =
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Drop This Course?",
                    html: `
                        <div style="text-align:left;">
                            <p>
                                Are you sure you want to drop:
                            </p>

                            <p>
                                <strong>
                                    ${courseTitle}
                                </strong>
                            </p>

                            <p style="margin-bottom:0;color:#6B7280;">
                                This will remove the course from
                                your current planned courses.
                            </p>
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText:
                        "Yes, Drop Course",
                    cancelButtonText:
                        "Keep Course",
                    reverseButtons: true,
                });

            if (!result.isConfirmed) {
                return;
            }

            try {
                setRemovingId(planId);

                await api.delete(
                    `/api/course-learning/plans/${planId}/remove/`
                );

                setPlans((current) =>
                    current.filter(
                        (currentItem) =>
                            getPlanId(
                                currentItem
                            ) !== planId
                    )
                );

                await cayepSwal.fire({
                    icon: "success",
                    title:
                        "Course Dropped",
                    text:
                        `${courseTitle} has been removed from your planned courses.`,
                    timer: 1800,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error(
                    "Drop course error:",
                    error
                );

                await fetchPlans();

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Unable to Drop Course",
                    text: getErrorMessage(
                        error,
                        "The course could not be removed from your planned courses."
                    ),
                });
            } finally {
                setRemovingId(null);
            }
        };

    const refreshPlans =
        async () => {
            setRefreshing(true);

            try {
                await fetchPlans();
            } finally {
                setRefreshing(false);
            }
        };

    const downloadBillSheet =
        async () => {
            if (!plans.length) {
                await cayepSwal.fire({
                    icon: "info",
                    title:
                        "No Planned Courses",
                    text:
                        "There are no planned courses available to generate a bill sheet.",
                });

                return;
            }

            try {
                setPdfLoading(true);

                const studentInformation =
                    plans.find(
                        (plan) =>
                            plan?.student_information
                    )?.student_information ||
                    null;

                const academicYear =
                    studentInformation
                        ?.current_academic_year ||
                    selectedYear?.name ||
                    selectedYear?.year ||
                    "Current Training Year";

                const blob =
                    await pdf(
                        <MyPlanCoursesBillSheetPDF
                            plans={plans}
                            selectedYear={
                                selectedYear
                            }
                            student={
                                studentInformation
                            }
                            academicYear={
                                academicYear
                            }
                        />
                    ).toBlob();

                const blobUrl =
                    URL.createObjectURL(
                        blob
                    );

                const link =
                    document.createElement(
                        "a"
                    );

                link.href =
                    blobUrl;

                const safeYear =
                    String(
                        academicYear
                    )
                        .replace(
                            /[\\/:*?"<>|]/g,
                            "-"
                        )
                        .replace(
                            /\s+/g,
                            "-"
                        );

                link.download =
                    `CAYEP-Planned-Courses-Bill-${safeYear}.pdf`;

                document.body.appendChild(
                    link
                );

                link.click();

                document.body.removeChild(
                    link
                );

                setTimeout(() => {
                    URL.revokeObjectURL(
                        blobUrl
                    );
                }, 1500);

                await cayepSwal.fire({
                    icon: "success",
                    title:
                        "Bill Sheet Generated",
                    text:
                        "Your planned courses bill sheet has been downloaded.",
                    timer: 1800,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error(
                    "Bill PDF error:",
                    error
                );

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "PDF Generation Failed",
                    text:
                        error?.message ||
                        "The planned courses bill sheet could not be generated.",
                });
            } finally {
                setPdfLoading(false);
            }
        };

    const categoryCount =
        new Set(
            plans
                .map(
                    (item) =>
                        getCourseFromPlan(
                            item
                        )?.category
                )
                .filter(Boolean)
        ).size;

    const totalValue =
        plans.reduce(
            (total, item) =>
                total +
                Number(
                    getCoursePrice(
                        item
                    ) || 0
                ),
            0
        );

    return (
        <div className="cayep-view-plans-page">
            <style>{`
                .cayep-view-plans-page {
                    min-height: 100vh;
                    background: #F4F7F9;
                    color: #142536;
                    padding: 24px;
                }

                .cayep-view-plans-container {
                    width: 100%;
                    max-width: 1450px;
                    margin: 0 auto;
                }

                .cayep-view-plans-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 18px;
                    margin-bottom: 22px;
                }

                .cayep-view-plans-heading {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .cayep-view-plans-heading-icon {
                    width: 54px;
                    height: 54px;
                    border-radius: 14px;
                    background: #062A4B;
                    color: #F6C944;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    flex-shrink: 0;
                    box-shadow: 0 5px 15px rgba(6, 42, 75, 0.12);
                }

                .cayep-view-plans-heading h1 {
                    margin: 0;
                    color: #062A4B;
                    font-size: 25px;
                    font-weight: 850;
                    line-height: 1.2;
                }

                .cayep-view-plans-heading p {
                    margin: 5px 0 0;
                    color: #6B7280;
                    font-size: 13px;
                }

                .cayep-view-plans-actions {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    flex-wrap: wrap;
                }

                .cayep-view-plans-refresh,
                .cayep-view-plans-pdf {
                    min-height: 43px;
                    border-radius: 9px;
                    padding: 0 14px;
                    font-size: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                }

                .cayep-view-plans-refresh {
                    border: 1px solid #D7E0E6;
                    background: #FFFFFF;
                    color: #062A4B;
                }

                .cayep-view-plans-refresh:hover {
                    border-color: #0F6DAB;
                    background: #F8FBFD;
                }

                .cayep-view-plans-pdf {
                    border: 1px solid #062A4B;
                    background: #062A4B;
                    color: #FFFFFF;
                }

                .cayep-view-plans-pdf:hover:not(:disabled) {
                    background: #063A6B;
                }

                .cayep-view-plans-pdf:disabled,
                .cayep-view-plans-refresh:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }

                .cayep-view-plans-summary {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 14px;
                    margin-bottom: 18px;
                }

                .cayep-summary-card {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 13px;
                    padding: 15px 17px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 3px 12px rgba(20, 37, 54, 0.035);
                }

                .cayep-summary-icon {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    background: #EEF5F9;
                    color: #0F6DAB;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .cayep-summary-card strong {
                    display: block;
                    color: #062A4B;
                    font-size: 19px;
                    font-weight: 900;
                }

                .cayep-summary-card span {
                    display: block;
                    margin-top: 2px;
                    color: #6B7280;
                    font-size: 11px;
                }

                .cayep-view-plans-toolbar {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 13px;
                    padding: 13px;
                    margin-bottom: 17px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .cayep-view-plans-search {
                    width: min(420px, 100%);
                    height: 43px;
                    border: 1px solid #D7E0E6;
                    border-radius: 9px;
                    background: #FFFFFF;
                    color: #142536;
                    padding: 0 13px;
                    outline: none;
                    font-size: 13px;
                }

                .cayep-view-plans-search:focus {
                    border-color: #0F6DAB;
                    box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
                }

                .cayep-view-plans-count {
                    color: #6B7280;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                }

                .cayep-plans-table-card {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 15px;
                    overflow: hidden;
                    box-shadow: 0 5px 16px rgba(20, 37, 54, 0.045);
                }

                .cayep-plans-table-wrapper {
                    width: 100%;
                    overflow-x: auto;
                }

                .cayep-plans-table {
                    width: 100%;
                    min-width: 920px;
                    border-collapse: collapse;
                }

                .cayep-plans-table thead {
                    background: #062A4B;
                }

                .cayep-plans-table th {
                    color: #FFFFFF;
                    text-align: left;
                    padding: 13px 14px;
                    font-size: 10px;
                    font-weight: 850;
                    text-transform: uppercase;
                    letter-spacing: 0.35px;
                    white-space: nowrap;
                }

                .cayep-plans-table td {
                    padding: 13px 14px;
                    border-top: 1px solid #E9EEF2;
                    vertical-align: middle;
                    font-size: 12px;
                }

                .cayep-plans-table tbody tr:hover {
                    background: #FBFCFD;
                }

                .cayep-course-cell {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    min-width: 250px;
                }

                .cayep-course-thumb {
                    width: 56px;
                    height: 48px;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #EEF2F4;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    color: #062A4B;
                }

                .cayep-course-thumb img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }

                .cayep-course-cell-title {
                    color: #062A4B;
                    font-size: 13px;
                    font-weight: 850;
                    line-height: 1.35;
                }

                .cayep-course-cell-description {
                    margin-top: 3px;
                    color: #8A929A;
                    font-size: 10px;
                    max-width: 300px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .cayep-info-cell {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: #142536;
                    font-size: 12px;
                    white-space: nowrap;
                }

                .cayep-info-cell svg {
                    color: #0F6DAB;
                    flex-shrink: 0;
                }

                .cayep-category-badge {
                    display: inline-flex;
                    align-items: center;
                    border-radius: 20px;
                    padding: 5px 8px;
                    background: #EEF5F9;
                    color: #0F6DAB;
                    font-size: 10px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .cayep-level-badge {
                    display: inline-flex;
                    align-items: center;
                    border-radius: 20px;
                    padding: 5px 8px;
                    background: #F7F1DE;
                    color: #8A6500;
                    font-size: 10px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .cayep-price {
                    color: #062A4B;
                    font-weight: 900;
                    white-space: nowrap;
                }

                .cayep-status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    border-radius: 20px;
                    padding: 5px 8px;
                    background: #EAF6E4;
                    color: #3C851C;
                    font-size: 10px;
                    font-weight: 850;
                    white-space: nowrap;
                }

                .cayep-drop-button {
                    min-height: 34px;
                    border: 1px solid #F0C5C5;
                    border-radius: 8px;
                    background: #FFF7F7;
                    color: #B42318;
                    padding: 0 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 850;
                    white-space: nowrap;
                }

                .cayep-drop-button:hover:not(:disabled) {
                    background: #FDECEC;
                    border-color: #E5A5A5;
                }

                .cayep-drop-button:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }

                .cayep-empty-plans {
                    padding: 70px 20px;
                    text-align: center;
                    color: #6B7280;
                }

                .cayep-empty-plans-icon {
                    color: #C4C7C7;
                    font-size: 42px;
                    margin-bottom: 12px;
                }

                .cayep-empty-plans strong {
                    display: block;
                    color: #142536;
                    font-size: 16px;
                    margin-bottom: 5px;
                }

                .cayep-empty-plans span {
                    display: block;
                    font-size: 12px;
                }

                .cayep-loading-plans {
                    min-height: 300px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #062A4B;
                    font-size: 13px;
                    font-weight: 800;
                }

                .cayep-year-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    color: #6B7280;
                    font-size: 11px;
                    font-weight: 700;
                    margin-top: 4px;
                }

                @media (max-width: 900px) {
                    .cayep-view-plans-summary {
                        grid-template-columns: 1fr;
                    }

                    .cayep-view-plans-header {
                        align-items: flex-start;
                    }
                }

                @media (max-width: 650px) {
                    .cayep-view-plans-page {
                        padding: 15px;
                    }

                    .cayep-view-plans-header {
                        flex-direction: column;
                    }

                    .cayep-view-plans-actions {
                        width: 100%;
                    }

                    .cayep-view-plans-refresh,
                    .cayep-view-plans-pdf {
                        flex: 1;
                    }

                    .cayep-view-plans-heading h1 {
                        font-size: 21px;
                    }

                    .cayep-view-plans-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .cayep-view-plans-search {
                        width: 100%;
                    }

                    .cayep-view-plans-count {
                        text-align: right;
                    }
                }
            `}</style>

            <div className="cayep-view-plans-container">

                <div className="cayep-view-plans-header">

                    <div className="cayep-view-plans-heading">

                        <div className="cayep-view-plans-heading-icon">
                            <FaGraduationCap />
                        </div>

                        <div>

                            <h1>
                                My Planned Courses
                            </h1>

                            <p>
                                View and manage the courses
                                you have selected for your
                                current training year.
                            </p>

                            {selectedYear?.name && (
                                <div className="cayep-year-label">
                                    <FaCalendarAlt />
                                    {selectedYear.name}
                                </div>
                            )}

                        </div>

                    </div>

                    <div className="cayep-view-plans-actions">

                        <button
                            type="button"
                            className="cayep-view-plans-refresh"
                            onClick={
                                refreshPlans
                            }
                            disabled={
                                loading ||
                                refreshing
                            }
                        >
                            {refreshing
                                ? "Refreshing..."
                                : "Refresh Courses"}
                        </button>

                        <button
                            type="button"
                            className="cayep-view-plans-pdf"
                            onClick={
                                downloadBillSheet
                            }
                            disabled={
                                pdfLoading ||
                                plans.length === 0
                            }
                        >
                            {pdfLoading ? (
                                <>
                                    <FaFilePdf />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FaDownload />
                                    Download Bill
                                </>
                            )}
                        </button>

                    </div>

                </div>

                <div className="cayep-view-plans-summary">

                    <div className="cayep-summary-card">

                        <div className="cayep-summary-icon">
                            <FaBookOpen />
                        </div>

                        <div>

                            <strong>
                                {plans.length}
                            </strong>

                            <span>
                                Planned Courses
                            </span>

                        </div>

                    </div>

                    <div className="cayep-summary-card">

                        <div className="cayep-summary-icon">
                            <FaLayerGroup />
                        </div>

                        <div>

                            <strong>
                                {categoryCount}
                            </strong>

                            <span>
                                Course Categories
                            </span>

                        </div>

                    </div>

                    <div className="cayep-summary-card">

                        <div className="cayep-summary-icon">
                            <FaGraduationCap />
                        </div>

                        <div>

                            <strong>
                                USD{" "}
                                {formatMoney(
                                    totalValue
                                )}
                            </strong>

                            <span>
                                Planned Course Value
                            </span>

                        </div>

                    </div>

                </div>

                <div className="cayep-view-plans-toolbar">

                    <input
                        type="search"
                        className="cayep-view-plans-search"
                        placeholder="Search your planned courses..."
                        value={search}
                        onChange={(event) =>
                            setSearch(
                                event.target.value
                            )
                        }
                    />

                    <div className="cayep-view-plans-count">
                        Showing{" "}
                        {
                            filteredPlans.length
                        }{" "}
                        of{" "}
                        {plans.length}{" "}
                        course
                        {plans.length === 1
                            ? ""
                            : "s"}
                    </div>

                </div>

                <div className="cayep-plans-table-card">

                    {loading ? (
                        <div className="cayep-loading-plans">
                            Loading your planned courses...
                        </div>
                    ) : filteredPlans.length === 0 ? (
                        <div className="cayep-empty-plans">

                            <FaBookOpen className="cayep-empty-plans-icon" />

                            <strong>
                                {plans.length === 0
                                    ? "You have no planned courses"
                                    : "No matching courses found"}
                            </strong>

                            <span>
                                {plans.length === 0
                                    ? "Courses you select from the course catalog will appear here."
                                    : "Try using a different search term."}
                            </span>

                        </div>
                    ) : (
                        <div className="cayep-plans-table-wrapper">

                            <table className="cayep-plans-table">

                                <thead>

                                    <tr>
                                        <th>Course</th>
                                        <th>Facilitator</th>
                                        <th>Category</th>
                                        <th>Level</th>
                                        <th>Duration</th>
                                        <th>Price</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {filteredPlans.map(
                                        (item) => {
                                            const course =
                                                getCourseFromPlan(
                                                    item
                                                );

                                            const planId =
                                                getPlanId(
                                                    item
                                                );

                                            const image =
                                                getCourseImage(
                                                    item
                                                );

                                            const title =
                                                getCourseTitle(
                                                    item
                                                );

                                            const description =
                                                course?.description ||
                                                "";

                                            return (
                                                <tr
                                                    key={
                                                        planId ||
                                                        getCourseId(
                                                            item
                                                        )
                                                    }
                                                >

                                                    <td>

                                                        <div className="cayep-course-cell">

                                                            <div className="cayep-course-thumb">

                                                                {image ? (
                                                                    <img
                                                                        src={
                                                                            image
                                                                        }
                                                                        alt={
                                                                            title
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <FaBookOpen />
                                                                )}

                                                            </div>

                                                            <div>

                                                                <div className="cayep-course-cell-title">
                                                                    {
                                                                        title
                                                                    }
                                                                </div>

                                                                {description && (
                                                                    <div className="cayep-course-cell-description">
                                                                        {
                                                                            description
                                                                        }
                                                                    </div>
                                                                )}

                                                            </div>

                                                        </div>

                                                    </td>

                                                    <td>

                                                        <div className="cayep-info-cell">

                                                            <FaUserTie />

                                                            <span>
                                                                {getCourseFacilitator(
                                                                    item
                                                                )}
                                                            </span>

                                                        </div>

                                                    </td>

                                                    <td>

                                                        {course?.category ? (
                                                            <span className="cayep-category-badge">
                                                                {
                                                                    course.category
                                                                }
                                                            </span>
                                                        ) : (
                                                            "—"
                                                        )}

                                                    </td>

                                                    <td>

                                                        {course?.level ? (
                                                            <span className="cayep-level-badge">
                                                                {
                                                                    course.level
                                                                }
                                                            </span>
                                                        ) : (
                                                            "—"
                                                        )}

                                                    </td>

                                                    <td>

                                                        <div className="cayep-info-cell">

                                                            <FaClock />

                                                            <span>
                                                                {course?.duration ||
                                                                    "Not specified"}
                                                            </span>

                                                        </div>

                                                    </td>

                                                    <td>

                                                        <span className="cayep-price">

                                                            {Number(
                                                                getCoursePrice(
                                                                    item
                                                                )
                                                            ) > 0
                                                                ? `USD ${formatMoney(
                                                                      getCoursePrice(
                                                                          item
                                                                      )
                                                                  )}`
                                                                : "Free"}

                                                        </span>

                                                    </td>

                                                    <td>

                                                        <span className="cayep-status-badge">

                                                            <FaBookOpen />

                                                            Planned

                                                        </span>

                                                    </td>

                                                    <td>

                                                        <button
                                                            type="button"
                                                            className="cayep-drop-button"
                                                            disabled={
                                                                !planId ||
                                                                removingId ===
                                                                    planId
                                                            }
                                                            onClick={() =>
                                                                removeCourse(
                                                                    item
                                                                )
                                                            }
                                                        >

                                                            <FaTrash />

                                                            {removingId ===
                                                            planId
                                                                ? "Dropping..."
                                                                : "Drop Course"}

                                                        </button>

                                                    </td>

                                                </tr>
                                            );
                                        }
                                    )}

                                </tbody>

                            </table>

                        </div>
                    )}

                </div>

            </div>

        </div>
    );
};

export default ViewPlanCoursesList;





// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useState,
// } from "react";
// import Swal from "sweetalert2";

// import {
//     FaBookOpen,
//     FaCalendarAlt,
//     FaClock,
//     FaGraduationCap,
//     FaLayerGroup,
//     FaTrash,
//     FaUserTie,
//     FaUsers,
//     FaVideo,
// } from "react-icons/fa";

// import api from "../../api/api";
// import { useAcademicYear } from "../../AcademicYearContext";

// const cayepSwal = Swal.mixin({
//     confirmButtonColor: "#062A4B",
//     cancelButtonColor: "#6B7280",
//     background: "#FFFFFF",
//     color: "#142536",
// });

// const getErrorMessage = (
//     error,
//     fallback = "Something went wrong."
// ) => {
//     const data = error?.response?.data;

//     if (!data) {
//         return error?.message || fallback;
//     }

//     if (typeof data === "string") {
//         return data;
//     }

//     if (data.detail) {
//         return data.detail;
//     }

//     if (data.message) {
//         return data.message;
//     }

//     if (data.error) {
//         return data.error;
//     }

//     if (data.non_field_errors?.length) {
//         return data.non_field_errors[0];
//     }

//     const firstKey = Object.keys(data)[0];

//     if (firstKey) {
//         const value = data[firstKey];

//         if (Array.isArray(value)) {
//             return value[0];
//         }

//         if (typeof value === "string") {
//             return value;
//         }

//         if (
//             value &&
//             typeof value === "object"
//         ) {
//             if (value.detail) {
//                 return value.detail;
//             }

//             if (value.message) {
//                 return value.message;
//             }
//         }
//     }

//     return fallback;
// };

// const formatMoney = (value) => {
//     const amount = Number(value || 0);

//     if (!Number.isFinite(amount)) {
//         return "0.00";
//     }

//     return new Intl.NumberFormat("en-US", {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 2,
//     }).format(amount);
// };

// const getCourseFromPlan = (item) => {
//     if (!item) {
//         return null;
//     }

//     return (
//         item.course ||
//         item.subject ||
//         null
//     );
// };

// const getPlanId = (item) => {
//     if (!item) {
//         return null;
//     }

//     const value =
//         item.id ??
//         item.plan_id ??
//         item.course_plan_id ??
//         null;

//     if (value == null) {
//         return null;
//     }

//     const number = Number(value);

//     return Number.isFinite(number)
//         ? number
//         : null;
// };

// const getCourseId = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     if (course?.id != null) {
//         return Number(course.id);
//     }

//     if (item?.course_id != null) {
//         return Number(item.course_id);
//     }

//     if (item?.subject_id != null) {
//         return Number(item.subject_id);
//     }

//     return null;
// };

// const normalizePlans = (items) => {
//     if (!Array.isArray(items)) {
//         return [];
//     }

//     const seen = new Set();

//     return items.filter((item) => {
//         const planId = getPlanId(item);
//         const courseId = getCourseId(item);

//         const identifier =
//             planId != null
//                 ? `plan-${planId}`
//                 : courseId != null
//                 ? `course-${courseId}`
//                 : null;

//         if (!identifier) {
//             return true;
//         }

//         if (seen.has(identifier)) {
//             return false;
//         }

//         seen.add(identifier);

//         return true;
//     });
// };

// const getCourseTitle = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     return (
//         course?.course_title ||
//         course?.name ||
//         item?.course_name ||
//         "Untitled Course"
//     );
// };

// const getCourseImage = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     return (
//         course?.thumbnail ||
//         course?.image ||
//         null
//     );
// };

// const getCoursePrice = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     return (
//         course?.current_price ??
//         course?.price ??
//         item?.current_price ??
//         0
//     );
// };

// const getCourseFacilitator = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     return (
//         course?.facilitator ||
//         course?.facilitator_name ||
//         item?.facilitator_name ||
//         "Not assigned"
//     );
// };

// const ViewPlanCoursesList = () => {
//     const { selectedYear } =
//         useAcademicYear();

//     const [plans, setPlans] =
//         useState([]);

//     const [loading, setLoading] =
//         useState(true);

//     const [removingId, setRemovingId] =
//         useState(null);

//     const [search, setSearch] =
//         useState("");

//     const [refreshing, setRefreshing] =
//         useState(false);

//     const fetchPlans =
//         useCallback(async () => {
//             setLoading(true);

//             try {
//                 const response =
//                     await api.get(
//                         "/api/course-learning/plans/"
//                     );

//                 const data =
//                     response.data;

//                 const items =
//                     Array.isArray(data)
//                         ? data
//                         : data?.items ||
//                           data?.results ||
//                           [];

//                 setPlans(
//                     normalizePlans(items)
//                 );
//             } catch (error) {
//                 console.error(
//                     "Planned courses error:",
//                     error
//                 );

//                 setPlans([]);

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Load Courses",
//                     text: getErrorMessage(
//                         error,
//                         "Your planned courses could not be loaded."
//                     ),
//                 });
//             } finally {
//                 setLoading(false);
//             }
//         }, []);

//     useEffect(() => {
//         fetchPlans();
//     }, [
//         fetchPlans,
//         selectedYear,
//     ]);

//     const filteredPlans =
//         useMemo(() => {
//             const value =
//                 search
//                     .trim()
//                     .toLowerCase();

//             if (!value) {
//                 return plans;
//             }

//             return plans.filter(
//                 (item) => {
//                     const course =
//                         getCourseFromPlan(
//                             item
//                         );

//                     const searchable = [
//                         course?.course_title,
//                         course?.name,
//                         course?.description,
//                         course?.category,
//                         course?.level,
//                         course?.facilitator,
//                         course?.facilitator_name,
//                         item?.course_name,
//                         item?.facilitator_name,
//                     ]
//                         .filter(Boolean)
//                         .join(" ")
//                         .toLowerCase();

//                     return searchable.includes(
//                         value
//                     );
//                 }
//             );
//         }, [
//             plans,
//             search,
//         ]);

//     const removeCourse =
//         async (item) => {
//             const planId =
//                 getPlanId(item);

//             const courseTitle =
//                 getCourseTitle(item);

//             if (!planId) {
//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Identify Course",
//                     text:
//                         "The selected course plan could not be identified.",
//                 });

//                 return;
//             }

//             const result =
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Drop This Course?",
//                     html: `
//                         <div style="text-align:left;">
//                             <p>
//                                 Are you sure you want to drop:
//                             </p>
//                             <p>
//                                 <strong>${courseTitle}</strong>
//                             </p>
//                             <p style="margin-bottom:0;color:#6B7280;">
//                                 This will remove the course from
//                                 your current planned courses.
//                             </p>
//                         </div>
//                     `,
//                     showCancelButton: true,
//                     confirmButtonText:
//                         "Yes, Drop Course",
//                     cancelButtonText:
//                         "Keep Course",
//                     reverseButtons: true,
//                 });

//             if (!result.isConfirmed) {
//                 return;
//             }

//             try {
//                 setRemovingId(planId);

//                 await api.delete(
//                     `/api/course-learning/plans/${planId}/remove/`
//                 );

//                 setPlans((current) =>
//                     current.filter(
//                         (currentItem) =>
//                             getPlanId(
//                                 currentItem
//                             ) !== planId
//                     )
//                 );

//                 await cayepSwal.fire({
//                     icon: "success",
//                     title:
//                         "Course Dropped",
//                     text: `${courseTitle} has been removed from your planned courses.`,
//                     timer: 1800,
//                     showConfirmButton: false,
//                 });
//             } catch (error) {
//                 console.error(
//                     "Drop course error:",
//                     error
//                 );

//                 await fetchPlans();

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Drop Course",
//                     text: getErrorMessage(
//                         error,
//                         "The course could not be removed from your planned courses."
//                     ),
//                 });
//             } finally {
//                 setRemovingId(null);
//             }
//         };

//     const refreshPlans =
//         async () => {
//             setRefreshing(true);

//             try {
//                 await fetchPlans();
//             } finally {
//                 setRefreshing(false);
//             }
//         };

//     return (
//         <div className="cayep-view-plans-page">
//             <style>{`
//                 .cayep-view-plans-page {
//                     min-height: 100vh;
//                     background: #F4F7F9;
//                     color: #142536;
//                     padding: 24px;
//                 }

//                 .cayep-view-plans-container {
//                     width: 100%;
//                     max-width: 1450px;
//                     margin: 0 auto;
//                 }

//                 .cayep-view-plans-header {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 18px;
//                     margin-bottom: 22px;
//                 }

//                 .cayep-view-plans-heading {
//                     display: flex;
//                     align-items: center;
//                     gap: 14px;
//                 }

//                 .cayep-view-plans-heading-icon {
//                     width: 54px;
//                     height: 54px;
//                     border-radius: 14px;
//                     background: #062A4B;
//                     color: #F6C944;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     font-size: 22px;
//                     flex-shrink: 0;
//                     box-shadow: 0 5px 15px rgba(6, 42, 75, 0.12);
//                 }

//                 .cayep-view-plans-heading h1 {
//                     margin: 0;
//                     color: #062A4B;
//                     font-size: 25px;
//                     font-weight: 850;
//                     line-height: 1.2;
//                 }

//                 .cayep-view-plans-heading p {
//                     margin: 5px 0 0;
//                     color: #6B7280;
//                     font-size: 13px;
//                 }

//                 .cayep-view-plans-actions {
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                 }

//                 .cayep-view-plans-refresh {
//                     min-height: 43px;
//                     border: 1px solid #D7E0E6;
//                     border-radius: 9px;
//                     background: #FFFFFF;
//                     color: #062A4B;
//                     padding: 0 14px;
//                     font-size: 12px;
//                     font-weight: 800;
//                     cursor: pointer;
//                 }

//                 .cayep-view-plans-refresh:hover {
//                     border-color: #0F6DAB;
//                     background: #F8FBFD;
//                 }

//                 .cayep-view-plans-summary {
//                     display: grid;
//                     grid-template-columns: repeat(3, minmax(0, 1fr));
//                     gap: 14px;
//                     margin-bottom: 18px;
//                 }

//                 .cayep-summary-card {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 13px;
//                     padding: 15px 17px;
//                     display: flex;
//                     align-items: center;
//                     gap: 12px;
//                     box-shadow: 0 3px 12px rgba(20, 37, 54, 0.035);
//                 }

//                 .cayep-summary-icon {
//                     width: 42px;
//                     height: 42px;
//                     border-radius: 10px;
//                     background: #EEF5F9;
//                     color: #0F6DAB;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     flex-shrink: 0;
//                 }

//                 .cayep-summary-card strong {
//                     display: block;
//                     color: #062A4B;
//                     font-size: 19px;
//                     font-weight: 900;
//                 }

//                 .cayep-summary-card span {
//                     display: block;
//                     margin-top: 2px;
//                     color: #6B7280;
//                     font-size: 11px;
//                 }

//                 .cayep-view-plans-toolbar {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 13px;
//                     padding: 13px;
//                     margin-bottom: 17px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 12px;
//                 }

//                 .cayep-view-plans-search {
//                     width: min(420px, 100%);
//                     height: 43px;
//                     border: 1px solid #D7E0E6;
//                     border-radius: 9px;
//                     background: #FFFFFF;
//                     color: #142536;
//                     padding: 0 13px;
//                     outline: none;
//                     font-size: 13px;
//                 }

//                 .cayep-view-plans-search:focus {
//                     border-color: #0F6DAB;
//                     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
//                 }

//                 .cayep-view-plans-count {
//                     color: #6B7280;
//                     font-size: 12px;
//                     font-weight: 700;
//                     white-space: nowrap;
//                 }

//                 .cayep-plans-table-card {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 15px;
//                     overflow: hidden;
//                     box-shadow: 0 5px 16px rgba(20, 37, 54, 0.045);
//                 }

//                 .cayep-plans-table-wrapper {
//                     width: 100%;
//                     overflow-x: auto;
//                 }

//                 .cayep-plans-table {
//                     width: 100%;
//                     min-width: 920px;
//                     border-collapse: collapse;
//                 }

//                 .cayep-plans-table thead {
//                     background: #062A4B;
//                 }

//                 .cayep-plans-table th {
//                     color: #FFFFFF;
//                     text-align: left;
//                     padding: 13px 14px;
//                     font-size: 10px;
//                     font-weight: 850;
//                     text-transform: uppercase;
//                     letter-spacing: 0.35px;
//                     white-space: nowrap;
//                 }

//                 .cayep-plans-table td {
//                     padding: 13px 14px;
//                     border-top: 1px solid #E9EEF2;
//                     vertical-align: middle;
//                     font-size: 12px;
//                 }

//                 .cayep-plans-table tbody tr:hover {
//                     background: #FBFCFD;
//                 }

//                 .cayep-course-cell {
//                     display: flex;
//                     align-items: center;
//                     gap: 11px;
//                     min-width: 250px;
//                 }

//                 .cayep-course-thumb {
//                     width: 56px;
//                     height: 48px;
//                     border-radius: 8px;
//                     overflow: hidden;
//                     background: #EEF2F4;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     flex-shrink: 0;
//                     color: #062A4B;
//                 }

//                 .cayep-course-thumb img {
//                     width: 100%;
//                     height: 100%;
//                     object-fit: cover;
//                     display: block;
//                 }

//                 .cayep-course-cell-title {
//                     color: #062A4B;
//                     font-size: 13px;
//                     font-weight: 850;
//                     line-height: 1.35;
//                 }

//                 .cayep-course-cell-description {
//                     margin-top: 3px;
//                     color: #8A929A;
//                     font-size: 10px;
//                     max-width: 300px;
//                     white-space: nowrap;
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                 }

//                 .cayep-info-cell {
//                     display: flex;
//                     align-items: center;
//                     gap: 7px;
//                     color: #142536;
//                     font-size: 12px;
//                     white-space: nowrap;
//                 }

//                 .cayep-info-cell svg {
//                     color: #0F6DAB;
//                     flex-shrink: 0;
//                 }

//                 .cayep-category-badge {
//                     display: inline-flex;
//                     align-items: center;
//                     border-radius: 20px;
//                     padding: 5px 8px;
//                     background: #EEF5F9;
//                     color: #0F6DAB;
//                     font-size: 10px;
//                     font-weight: 800;
//                     white-space: nowrap;
//                 }

//                 .cayep-level-badge {
//                     display: inline-flex;
//                     align-items: center;
//                     border-radius: 20px;
//                     padding: 5px 8px;
//                     background: #F7F1DE;
//                     color: #8A6500;
//                     font-size: 10px;
//                     font-weight: 800;
//                     white-space: nowrap;
//                 }

//                 .cayep-price {
//                     color: #062A4B;
//                     font-weight: 900;
//                     white-space: nowrap;
//                 }

//                 .cayep-status-badge {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 5px;
//                     border-radius: 20px;
//                     padding: 5px 8px;
//                     background: #EAF6E4;
//                     color: #3C851C;
//                     font-size: 10px;
//                     font-weight: 850;
//                     white-space: nowrap;
//                 }

//                 .cayep-drop-button {
//                     min-height: 34px;
//                     border: 1px solid #F0C5C5;
//                     border-radius: 8px;
//                     background: #FFF7F7;
//                     color: #B42318;
//                     padding: 0 10px;
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 6px;
//                     cursor: pointer;
//                     font-size: 11px;
//                     font-weight: 850;
//                     white-space: nowrap;
//                 }

//                 .cayep-drop-button:hover:not(:disabled) {
//                     background: #FDECEC;
//                     border-color: #E5A5A5;
//                 }

//                 .cayep-drop-button:disabled {
//                     opacity: 0.55;
//                     cursor: not-allowed;
//                 }

//                 .cayep-empty-plans {
//                     padding: 70px 20px;
//                     text-align: center;
//                     color: #6B7280;
//                 }

//                 .cayep-empty-plans-icon {
//                     color: #C4C7C7;
//                     font-size: 42px;
//                     margin-bottom: 12px;
//                 }

//                 .cayep-empty-plans strong {
//                     display: block;
//                     color: #142536;
//                     font-size: 16px;
//                     margin-bottom: 5px;
//                 }

//                 .cayep-empty-plans span {
//                     display: block;
//                     font-size: 12px;
//                 }

//                 .cayep-loading-plans {
//                     min-height: 300px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     color: #062A4B;
//                     font-size: 13px;
//                     font-weight: 800;
//                 }

//                 .cayep-year-label {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 6px;
//                     color: #6B7280;
//                     font-size: 11px;
//                     font-weight: 700;
//                     margin-top: 4px;
//                 }

//                 @media (max-width: 900px) {
//                     .cayep-view-plans-summary {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-view-plans-header {
//                         align-items: flex-start;
//                     }
//                 }

//                 @media (max-width: 650px) {
//                     .cayep-view-plans-page {
//                         padding: 15px;
//                     }

//                     .cayep-view-plans-header {
//                         flex-direction: column;
//                     }

//                     .cayep-view-plans-actions {
//                         width: 100%;
//                     }

//                     .cayep-view-plans-refresh {
//                         width: 100%;
//                     }

//                     .cayep-view-plans-heading h1 {
//                         font-size: 21px;
//                     }

//                     .cayep-view-plans-toolbar {
//                         flex-direction: column;
//                         align-items: stretch;
//                     }

//                     .cayep-view-plans-search {
//                         width: 100%;
//                     }

//                     .cayep-view-plans-count {
//                         text-align: right;
//                     }
//                 }
//             `}</style>

//             <div className="cayep-view-plans-container">
//                 <div className="cayep-view-plans-header">
//                     <div className="cayep-view-plans-heading">
//                         <div className="cayep-view-plans-heading-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div>
//                             <h1>
//                                 My Planned Courses
//                             </h1>

//                             <p>
//                                 View and manage the courses
//                                 you have selected for your
//                                 current training year.
//                             </p>

//                             {selectedYear?.name && (
//                                 <div className="cayep-year-label">
//                                     <FaCalendarAlt />
//                                     {selectedYear.name}
//                                 </div>
//                             )}
//                         </div>
//                     </div>

//                     <div className="cayep-view-plans-actions">
//                         <button
//                             type="button"
//                             className="cayep-view-plans-refresh"
//                             onClick={
//                                 refreshPlans
//                             }
//                             disabled={
//                                 loading ||
//                                 refreshing
//                             }
//                         >
//                             {refreshing
//                                 ? "Refreshing..."
//                                 : "Refresh Courses"}
//                         </button>
//                     </div>
//                 </div>

//                 <div className="cayep-view-plans-summary">
//                     <div className="cayep-summary-card">
//                         <div className="cayep-summary-icon">
//                             <FaBookOpen />
//                         </div>

//                         <div>
//                             <strong>
//                                 {plans.length}
//                             </strong>

//                             <span>
//                                 Planned Courses
//                             </span>
//                         </div>
//                     </div>

//                     <div className="cayep-summary-card">
//                         <div className="cayep-summary-icon">
//                             <FaLayerGroup />
//                         </div>

//                         <div>
//                             <strong>
//                                 {
//                                     new Set(
//                                         plans
//                                             .map(
//                                                 (item) =>
//                                                     getCourseFromPlan(
//                                                         item
//                                                     )?.category
//                                             )
//                                             .filter(
//                                                 Boolean
//                                             )
//                                     ).size
//                                 }
//                             </strong>

//                             <span>
//                                 Course Categories
//                             </span>
//                         </div>
//                     </div>

//                     <div className="cayep-summary-card">
//                         <div className="cayep-summary-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div>
//                             <strong>
//                                 LRD{" "}
//                                 {formatMoney(
//                                     plans.reduce(
//                                         (
//                                             total,
//                                             item
//                                         ) =>
//                                             total +
//                                             Number(
//                                                 getCoursePrice(
//                                                     item
//                                                 ) || 0
//                                             ),
//                                         0
//                                     )
//                                 )}
//                             </strong>

//                             <span>
//                                 Planned Course Value
//                             </span>
//                         </div>
//                     </div>
//                 </div>

//                 <div className="cayep-view-plans-toolbar">
//                     <input
//                         type="search"
//                         className="cayep-view-plans-search"
//                         placeholder="Search your planned courses..."
//                         value={search}
//                         onChange={(event) =>
//                             setSearch(
//                                 event.target.value
//                             )
//                         }
//                     />

//                     <div className="cayep-view-plans-count">
//                         Showing{" "}
//                         {
//                             filteredPlans.length
//                         }{" "}
//                         of{" "}
//                         {plans.length}{" "}
//                         course
//                         {plans.length === 1
//                             ? ""
//                             : "s"}
//                     </div>
//                 </div>

//                 <div className="cayep-plans-table-card">
//                     {loading ? (
//                         <div className="cayep-loading-plans">
//                             Loading your planned courses...
//                         </div>
//                     ) : filteredPlans.length ===
//                       0 ? (
//                         <div className="cayep-empty-plans">
//                             <FaBookOpen className="cayep-empty-plans-icon" />

//                             <strong>
//                                 {plans.length ===
//                                 0
//                                     ? "You have no planned courses"
//                                     : "No matching courses found"}
//                             </strong>

//                             <span>
//                                 {plans.length ===
//                                 0
//                                     ? "Courses you select from the course catalog will appear here."
//                                     : "Try using a different search term."}
//                             </span>
//                         </div>
//                     ) : (
//                         <div className="cayep-plans-table-wrapper">
//                             <table className="cayep-plans-table">
//                                 <thead>
//                                     <tr>
//                                         <th>
//                                             Course
//                                         </th>

//                                         <th>
//                                             Facilitator
//                                         </th>

//                                         <th>
//                                             Category
//                                         </th>

//                                         <th>
//                                             Level
//                                         </th>

//                                         <th>
//                                             Duration
//                                         </th>

//                                         <th>
//                                             Price
//                                         </th>

//                                         <th>
//                                             Status
//                                         </th>

//                                         <th>
//                                             Action
//                                         </th>
//                                     </tr>
//                                 </thead>

//                                 <tbody>
//                                     {filteredPlans.map(
//                                         (
//                                             item
//                                         ) => {
//                                             const course =
//                                                 getCourseFromPlan(
//                                                     item
//                                                 );

//                                             const planId =
//                                                 getPlanId(
//                                                     item
//                                                 );

//                                             const image =
//                                                 getCourseImage(
//                                                     item
//                                                 );

//                                             const title =
//                                                 getCourseTitle(
//                                                     item
//                                                 );

//                                             const description =
//                                                 course?.description ||
//                                                 "";

//                                             return (
//                                                 <tr
//                                                     key={
//                                                         planId ||
//                                                         getCourseId(
//                                                             item
//                                                         )
//                                                     }
//                                                 >
//                                                     <td>
//                                                         <div className="cayep-course-cell">
//                                                             <div className="cayep-course-thumb">
//                                                                 {image ? (
//                                                                     <img
//                                                                         src={
//                                                                             image
//                                                                         }
//                                                                         alt={
//                                                                             title
//                                                                         }
//                                                                     />
//                                                                 ) : (
//                                                                     <FaBookOpen />
//                                                                 )}
//                                                             </div>

//                                                             <div>
//                                                                 <div className="cayep-course-cell-title">
//                                                                     {
//                                                                         title
//                                                                     }
//                                                                 </div>

//                                                                 {description && (
//                                                                     <div className="cayep-course-cell-description">
//                                                                         {
//                                                                             description
//                                                                         }
//                                                                     </div>
//                                                                 )}
//                                                             </div>
//                                                         </div>
//                                                     </td>

//                                                     <td>
//                                                         <div className="cayep-info-cell">
//                                                             <FaUserTie />

//                                                             <span>
//                                                                 {getCourseFacilitator(
//                                                                     item
//                                                                 )}
//                                                             </span>
//                                                         </div>
//                                                     </td>

//                                                     <td>
//                                                         {course?.category ? (
//                                                             <span className="cayep-category-badge">
//                                                                 {
//                                                                     course.category
//                                                                 }
//                                                             </span>
//                                                         ) : (
//                                                             "—"
//                                                         )}
//                                                     </td>

//                                                     <td>
//                                                         {course?.level ? (
//                                                             <span className="cayep-level-badge">
//                                                                 {
//                                                                     course.level
//                                                                 }
//                                                             </span>
//                                                         ) : (
//                                                             "—"
//                                                         )}
//                                                     </td>

//                                                     <td>
//                                                         <div className="cayep-info-cell">
//                                                             <FaClock />

//                                                             <span>
//                                                                 {course?.duration ||
//                                                                     "Not specified"}
//                                                             </span>
//                                                         </div>
//                                                     </td>

//                                                     <td>
//                                                         <span className="cayep-price">
//                                                             {Number(
//                                                                 getCoursePrice(
//                                                                     item
//                                                                 )
//                                                             ) >
//                                                             0
//                                                                 ? `LRD ${formatMoney(
//                                                                       getCoursePrice(
//                                                                           item
//                                                                       )
//                                                                   )}`
//                                                                 : "Free"}
//                                                         </span>
//                                                     </td>

//                                                     <td>
//                                                         <span className="cayep-status-badge">
//                                                             <FaBookOpen />
//                                                             Planned
//                                                         </span>
//                                                     </td>

//                                                     <td>
//                                                         <button
//                                                             type="button"
//                                                             className="cayep-drop-button"
//                                                             disabled={
//                                                                 !planId ||
//                                                                 removingId ===
//                                                                     planId
//                                                             }
//                                                             onClick={() =>
//                                                                 removeCourse(
//                                                                     item
//                                                                 )
//                                                             }
//                                                         >
//                                                             <FaTrash />

//                                                             {removingId ===
//                                                             planId
//                                                                 ? "Dropping..."
//                                                                 : "Drop Course"}
//                                                         </button>
//                                                     </td>
//                                                 </tr>
//                                             );
//                                         }
//                                     )}
//                                 </tbody>
//                             </table>
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default ViewPlanCoursesList;